import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faShieldAlt,
  faStethoscope,
  faTimes,
  faCheckCircle,
  faEdit,
  faSave,
  faPlus,
  faTrash,
  faHospital,
  faUserMd,
  faBed,
  faInfoCircle,
  faSpinner,
  faQuestionCircle,
  faFilter,
} from "@fortawesome/free-solid-svg-icons";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import axios from "axios";
import { toast } from "react-toastify";
import { debounce } from "lodash"; // For debouncing search; install if needed
import { BASE_URL } from "../../config";
import { customStyles } from "./selectStyles"; // Your custom react-select styles
import Type3ContinueModalActionButton from "./Type3ContinueModalActionButton";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

// Room Options
const roomOptions = [
  { value: "ward", label: "Ward" },
  { value: "semi-private", label: "Semi-Private" },
  { value: "private", label: "Private" },
  { value: "suite", label: "Suite" },
  { value: "icu", label: "ICU" },
];
// PhilHealth Options
const philHealthOptions = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];
// Relationship Options (static; make searchable if needed)
const relationshipOptions = [
  { value: "spouse", label: "Spouse" },
  { value: "child", label: "Child" },
  { value: "parent", label: "Parent" },
  { value: "sibling", label: "Sibling" },
  { value: "other", label: "Other" },
];

// Enhanced API response handler with better error handling
const handleApiResponse = (
  response,
  successCallback = null,
  errorCallback = null
) => {
  try {
    // Handle Axios response or direct data (string/object/array)
    let data;
    if (response?.data) {
      data = response.data;
    } else if (typeof response == "object" && response !== null) {
      data = response;
    } else if (typeof response == "string") {
      data = { status: "success", message: response };
    } else {
      const errorMsg = "Invalid response received";
      toast.error(errorMsg);
      console.error("Invalid response:", response);
      if (errorCallback) errorCallback(response);
      return;
    }

    // If it's a plain string, treat as success message
    if (typeof data == "string") {
      toast.info(data);
      if (successCallback) successCallback(null);
      return;
    }

    // If it's a plain object/array without status, assume success
    if (
      typeof data == "object" &&
      data !== null &&
      !data.status &&
      (Array.isArray(data) || Object.keys(data).length > 0)
    ) {
      // continue here
      const message =
        data.message ||
        `Operation completed: ${
          Array.isArray(data) ? data.length : Object.keys(data).length
        } items`;
      toast.success(message);
      if (successCallback) successCallback(data);
      return;
    }

    // Standard structure check
    if (data.status == "success") {
      const message = data.message || "Success";
      toast.success(message);
      if (successCallback) successCallback(data.data || data);
    } else if (data.status == "error") {
      const message = data.message || "An error occurred";

      // Enhanced error extraction
      let errorMessages = [message];

      // Extract all validation errors from errors object
      if (data.data?.errors && typeof data.data.errors == "object") {
        const allErrors = [];

        // Flatten all error arrays
        Object.values(data.data.errors).forEach((errorArray) => {
          if (Array.isArray(errorArray)) {
            allErrors.push(...errorArray);
          } else if (typeof errorArray == "string") {
            allErrors.push(errorArray);
          }
        });

        if (allErrors.length > 0) {
          errorMessages = allErrors;
        }
      }

      // Show all errors (first error as toast, others in console)
      if (errorMessages.length > 0) {
        toast.error(errorMessages[0]);

        // Log additional errors to console
        if (errorMessages.length > 1) {
          console.error(
            "Additional validation errors:",
            errorMessages.slice(1)
          );
        }

        // Show error count in console for better debugging
        console.error(`API Error: ${message}`, {
          totalErrors: errorMessages.length,
          validationErrors: data.data?.errors || {},
          fullResponse: data,
        });
      }

      if (errorCallback) errorCallback(data, errorMessages);
    } else {
      // Fallback for unexpected shapes
      const warningMsg = "Unexpected response format";
      toast.warning(warningMsg);
      console.warn("Unexpected API response:", {
        response: data,
        expected: ["status", "message", "data"],
        received: Object.keys(data),
      });
      if (successCallback) successCallback(data);
    }
  } catch (err) {
    const errorMsg = "Network or parsing error";
    toast.error(errorMsg);
    console.error("API handler error:", {
      error: err,
      response: response,
      stack: err.stack,
    });
    if (errorCallback) errorCallback(err);
  }
};

// Alternative version that shows multiple errors as list
// const handleApiResponseWithMultipleErrors = (
//   response,
//   successCallback = null,
//   errorCallback = null
// ) => {
//   try {
//     // ... (same initial processing as above)

//     if (data.status == "error") {
//       const message = data.message || "An error occurred";

//       // Collect all error messages
//       let allErrorMessages = [message];

//       if (data.data?.errors && typeof data.data.errors == "object") {
//         const validationErrors = [];

//         Object.values(data.data.errors).forEach(errorArray => {
//           if (Array.isArray(errorArray)) {
//             validationErrors.push(...errorArray);
//           } else if (typeof errorArray == 'string') {
//             validationErrors.push(errorArray);
//           }
//         });

//         if (validationErrors.length > 0) {
//           allErrorMessages = validationErrors;
//         }
//       }

//       // Show first error as toast
//       toast.error(allErrorMessages[0]);

//       // If multiple errors, show them in a detailed console log
//       if (allErrorMessages.length > 1) {
//         console.group("🔴 Multiple Validation Errors");
//         allErrorMessages.forEach((error, index) => {
//           console.error(`${index + 1}. ${error}`);
//         });
//         console.groupEnd();
//       }

//       // Enhanced logging for debugging
//       console.error("API Error Details:", {
//         mainMessage: message,
//         totalErrors: allErrorMessages.length,
//         validationErrors: data.data?.errors || {},
//         httpStatus: response?.status,
//         endpoint: response?.config?.url
//       });

//       if (errorCallback) errorCallback(data, allErrorMessages);
//     }

//     // ... (rest of the function remains same)
//   } catch (err) {
//     // ... (error handling same as above)
//   }
// };

const Type3ContinueModal = ({
  isOpen,
  onClose,
  transNo,
  selectedMember,
  selectedProvider,
}) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("patient-details");
  // const [editMode, setEditMode] = useState(false);
  const [editMode, setEditMode] = useState(true);
  const [data, setData] = useState({
    member_details: [],
    corp_plan_rnb: [],
    plan_data: [],
    ip_totals: [],
    icd_options: [],
    physician_options: [],
    copay: { copay_company_pct: 0, copay_member_pct: 100 },
    guaranteed_limit: 0,
    rnb_rows: [],
    transaction: {},
  });
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    amaphilAgent: "",
    personSpokenProvider: "",
    landline: "",
    personSpokenRelative: "",
    relationship: null,
    philHealthCovered: null,
    department: "",
    mobileDischarge: "",
    chiefComplaint: "",
    attendingPhysician: [], // Multi-select array
    initialDiagnosis: null, // Single ICD
    remainingLimitIcd: "",
    remark: "",
    reason: "",
    coverableTag: "1", // Default "Yes"
    rnbRows: [
      {
        id: 1,
        roomType: null,
        roomNo: "",
        roomRate: 0,
        daysConfined: 1,
        totalFee: 0,
      }, // Default first row, non-removable
    ],
    type: "admission", // Default; set to 'discharge' for that flow
    formNumber: "", // Hidden
  });
  const [chiefComplaintError, setChiefComplaintError] = useState("");
  const modalRef = useRef(null);
  const [shouldBump, setShouldBump] = useState(false);
  const [openRnbSelectId, setOpenRnbSelectId] = useState(null);
  const token = localStorage.getItem("authentication/token");
  const headers = { Authorization: `Bearer ${token}` };
  // Debounced load options
  // const loadIcdOptions = useCallback(
  //   debounce((inputValue, callback) => {
  //     if (inputValue.length < 2) {
  //       callback([]); // Min 2 chars for search
  //       return;
  //     }
  //     axios
  //       .get(`${BASE_URL}/voc/icd/search?q=${inputValue}`, { headers })
  //       .then((res) => {
  //         if (res.data.status == "success") {
  //           callback(
  //             res.data.data.map((item) => ({
  //               value: item.tbl_id,
  //               label: `${item.icd_10_cm} - ${item.condition}`,
  //             }))
  //           );
  //         } else callback([]);
  //       })
  //       .catch(() => callback([]));
  //   }, 300),
  //   [headers]
  // );
  const loadIcdOptions = useCallback(async (inputValue, callback) => {
    if (!inputValue || inputValue.length < 2) {
      callback([]);
      return;
    }
    try {
      const response = await axios.get(`${BASE_URL}/voc/icd/search`, {
        params: { q: inputValue },
        headers: {
          Authorization: `Bearer ${localStorage.getItem(
            "authentication/token"
          )}`,
        },
      });
      handleApiResponse(response, (apiData) => {
        if (apiData) {
          callback(
            apiData.map((item) => ({
              value: item.tbl_id || item.id,
              label: `${item.icd_10_cm || item.value} - ${
                item.condition || item.label
              }`,
            }))
          );
        } else {
          callback([]);
        }
      });
    } catch (error) {
      console.error("Failed to load ICDs:", error);
      callback([]);
    }
  }, []);
  const loadPhysicianOptions = useCallback(
    debounce((inputValue, callback) => {
      const providerCode =
        selectedProvider?.id || data.transaction.provider_code || "000023";
      const query = encodeURIComponent(inputValue || "");
      const encodedProviderCode = encodeURIComponent(providerCode);

      axios
        .get(
          `${BASE_URL}/voc/physicians/search/${encodedProviderCode}?q=${query}&limit=200`,
          { headers }
        )
        .then((res) => {
          const apiData = res.data?.data || [];
          callback(
            apiData.map((item) => ({
              value: item.physician_id || item.value || item.id,
              label: item.full_name || item.label || item.physician_id,
            }))
          );
        })
        .catch((err) => {
          console.error("Failed to load physicians:", err);
          callback([]);
        });
    }, 300),
    [selectedProvider?.id, data.transaction.provider_code, headers]
  );
  // Sa component cleanup
  // Debounced Functions cleanup
  // useEffect(() => {
  //   return () => {
  //     loadIcdOptions.cancel();
  //     loadPhysicianOptions.cancel();
  //   };
  // }, [loadIcdOptions, loadPhysicianOptions]);
  // Load initial data
  useEffect(() => {
    const abortController = new AbortController();
    if (isOpen && transNo) {
      setLoading(true);
      setChiefComplaintError("");
      axios
        .get(`${BASE_URL}/voc/load-initial/${transNo}`, { headers })
        .then((res) => {
          handleApiResponse(res, (apiData) => {
            if (apiData) {
              setData(apiData);
              const {
                transaction,
                ip_totals,
                copay,
                guaranteed_limit,
                rnb_rows,
              } = apiData;
              const ip = ip_totals[0] || {};
              setFormData((prev) => ({
                ...prev,
                chiefComplaint: transaction.chief_complaint || "",
                remainingLimitIcd: `₱${ip.remaining || 0}`,
                coverableTag: transaction.coverable_tag || "1",
                // rnbRows: rnb_rows || prev.rnbRows,
                // Pre-pop other fields from transaction if available
                amaphilAgent: transaction.amap_agent || "",
                personSpokenProvider: transaction.person_spoken || "",
                landline: transaction.provider_landline || "",
                personSpokenRelative: transaction.person_relationship || "",
                department: transaction.provider_department || "",
                mobileDischarge: transaction.mobile_no_discharge || "",
                remark: transaction.remarks || "",
                reason: transaction.reason || "",
                attendingPhysician: JSON.parse(
                  transaction.attending_physician || "[]"
                ),
                initialDiagnosis: transaction.initial_icd
                  ? { value: transaction.initial_icd, label: "" }
                  : null, // Fetch label via search if needed
              }));
            }
          });
        })
        .catch((err) =>
          handleApiResponse(err.response?.data || err, null, () =>
            setLoading(false)
          )
        )
        .finally(() => setLoading(false));
    }
    return () => abortController.abort();
    // }, [isOpen, transNo, headers]);
  }, [isOpen, transNo]);
  // Outside click & Escape
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        event.target instanceof Element &&
        event.target.closest(".rnb-room-select__menu")
      ) {
        return;
      }

      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setShouldBump(true);
        setTimeout(() => setShouldBump(false), 300);
      }
    };
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key == "Escape" && isOpen) onClose();
    };
    if (isOpen) document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);
  // Handlers
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };
  const handleSelectChange = (field, option) => {
    setFormData((prev) => ({ ...prev, [field]: option }));
  };
  const handleRadioChange = (value) => {
    setFormData((prev) => ({ ...prev, coverableTag: value }));
  };
  // const updateRnbRow = (rowId, field, value) => {
  //   setFormData((prev) => {
  //     const rows = prev.rnbRows.map((row) =>
  //       row.id == rowId
  //         ? {
  //             ...row,
  //             [field]: value,
  //             totalFee:
  //               field == "roomRate" || field == "daysConfined"
  //                 ? parseFloat(row.roomRate || 0) *
  //                   parseInt(row.daysConfined || 1)
  //                 : row.totalFee,
  //           }
  //         : row
  //     );
  //     return { ...prev, rnbRows: rows };
  //   });
  // };
  const updateRnbRow = useCallback((rowId, field, value) => {
    setFormData((prev) => {
      const rows = prev.rnbRows.map((row) => {
        if (row.id !== rowId) return row;
        const updatedRow = { ...row, [field]: value };
        // Compute totalFee only when relevant fields change
        if (field == "roomRate" || field == "daysConfined") {
          updatedRow.totalFee =
            parseFloat(updatedRow.roomRate || 0) *
            parseInt(updatedRow.daysConfined || 1);
        }
        return updatedRow;
      });
      return { ...prev, rnbRows: rows };
    });
  }, []);
  const handleChiefComplaintChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, chiefComplaint: value }));
    if (value.length < 2) {
      setChiefComplaintError("Minimum 2 characters required.");
    } else if (value.length > 1000) {
      setChiefComplaintError("Maximum 1000 characters exceeded.");
    } else {
      setChiefComplaintError("");
    }
  };
  const toggleEdit = () => setEditMode(!editMode);
  // const handleSave = () => {
  //   if (
  //     formData.chiefComplaint.length < 2 ||
  //     formData.chiefComplaint.length > 1000
  //   ) {
  //     toast.error("Chief Complaint must be 2-1000 characters.");
  //     return;
  //   }
  //   // Basic req check (expand as needed)
  //   if (!formData.amaphilAgent || !formData.department) {
  //     toast.error("Required fields missing.");
  //     return;
  //   }
  //   axios
  //     .post(`${BASE_URL}/voc/save/${transNo}`, formData, { headers })
  //     .then((res) => {
  //       handleApiResponse(res, () => {
  //         setEditMode(false);
  //         // Optionally refresh data here if needed
  //       });
  //     })
  //     .catch((err) => handleApiResponse(err.response?.data || err));
  // };

  // notes: actually same lang naman yung save and verify
  const handleSave = () => {
    return;
  };

  const addRnbRow = () => {
    if (formData.rnbRows.length >= 5) {
      toast.warning("Maximum 5 rows allowed.");
      return;
    }
    const newId = formData.rnbRows.length + 1;
    setFormData((prev) => ({
      ...prev,
      rnbRows: [
        ...prev.rnbRows,
        {
          id: newId,
          roomType: null,
          roomNo: "",
          roomRate: 0,
          daysConfined: 1,
          totalFee: 0,
        },
      ],
    }));
  };
  const removeRnbRow = (id) => {
    if (id == 1) {
      toast.warning("First row cannot be removed.");
      return;
    }
    setFormData((prev) => ({
      ...prev,
      rnbRows: prev.rnbRows.filter((row) => row.id !== id),
    }));
  };
  const handleVerify = () => {
    // comment out na itong handleSave, kasi almost same lang naman sila ni Verify functionality
    // handleSave(); // Save first, then verify
    if (chiefComplaintError) return;

    // Swal confirmation before verifying
    Swal.fire({
      title: "Confirm Verification",
      html: `
        <div style="text-align: left;">
          <p><strong>Once verified, you can no longer edit this.</strong></p>
          <p>You can only make edits in the Discharged section, so please confirm and review the data before proceeding with verification.</p>
        </div>

      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, verify!",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        const payload = {
          ...formData,
          type: formData.type, // 'admission' or 'discharge'
          attendingPhysician: formData.attendingPhysician.map((p) => p.value), // IDs only for implode
          initialDiagnosis: formData.initialDiagnosis?.value, // ID only
          rnbRows: formData.rnbRows, // Full array for backend slice/append
          philhealth_covered_tag:
            formData.philHealthCovered?.value == "yes" ? 1 : 0,
          coverable_tag: formData.coverableTag,
          // Discharge extras if type='discharge' (add fields/UI as needed)
          admission_date: formData.admission_date || null,
          discharged_date: formData.discharged_date || null,
          diagnosis: formData.diagnosis || null,
          proof: formData.proof || null,
          soa: formData.soa || null,
          claim_remarks: formData.claim_remarks || null,
        };
        axios
          .post(`${BASE_URL}/voc/verify/${transNo}`, payload, { headers })
          .then((res) => {
            handleApiResponse(res, (apiData) => {
              toast.success(res.data.message);
              if (apiData?.loa_no) toast.info(`LOA No: ${apiData.loa_no}`);
              // onClose();
              // Show redirecting toast
              toast.loading("Redirecting to Transactions List...", {
                duration: 1200, // toast stays 1.2s
              });
              // Delay the navigation so the toast appears first
              setTimeout(() => {
                navigate("/transaction-discharge");
              }, 2000); // 1 second delay
            });
          })
          .catch((err) => handleApiResponse(err.response?.data || err));
      }
    });
  };

  if (!isOpen) return null;
  const member = data.member_details[0] || {};
  const rnb = data.corp_plan_rnb[0] || {};
  const plan = data.plan_data[0] || {};
  const ip = data.ip_totals[0] || {};
  const transaction = data.transaction || {};
  const memberName = `${member.first_name || ""} ${
    member.middle_name ? member.middle_name + " " : ""
  }${member.last_name || ""}`.trim();
  const providerName =
    selectedProvider?.text || transaction.provider_name || "Provider";
  const copayCompany = data.copay?.copay_company_pct + "%" || "0%";
  const copayMember = data.copay?.copay_member_pct + "%" || "100%";
  const guaranteedLimit = data.guaranteed_limit || 0;
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-y-0 left-0 tracker-aware-modal-layer bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
      >
        <motion.div
          ref={modalRef}
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: shouldBump ? [1, 1.02, 1] : 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: shouldBump ? 0.3 : 0.2 }}
          className="bg-white rounded-2xl p-4 w-full max-w-5xl mx-4 max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-100"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-200">
            {" "}
            <h2 className="text-xl text-gray-900 flex items-center gap-2 flex-1">
              {" "}
              <FontAwesomeIcon icon={faHospital} className="text-blue-600" />
              <span className="text-xl">
                <b>In-Patient</b> Verification for <b>{memberName}</b> in{" "}
                <b>{providerName}</b> {" - "} TRN #<b>{transNo}</b>
              </span>
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1.5 rounded-full hover:bg-gray-100 ml-2"
            >
              <FontAwesomeIcon icon={faTimes} className="text-lg" />
            </button>
          </div>
          {loading ? (
            <div className="flex justify-center py-8">
              {" "}
              <FontAwesomeIcon
                icon={faSpinner}
                spin
                className="text-xl text-blue-600"
              />
            </div>
          ) : (
            <>
              {/* Tabs */}
              <div className="flex space-x-1 mb-1">
                <button
                  onClick={() => setActiveTab("patient-details")}
                  className={`px-6 py-3 font-medium transition-all duration-200 text-sm rounded-t-lg flex items-center space-x-2 ${
                    activeTab == "patient-details"
                      ? "bg-blue-600 text-white shadow-md"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800"
                  }`}
                >
                  {/* User Profile Icon */}
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                  <span>Patient Details</span>
                </button>
                <button
                  onClick={() => setActiveTab("admission-information")}
                  className={`px-6 py-3 font-medium transition-all duration-200 text-sm rounded-t-lg flex items-center space-x-2 ${
                    activeTab == "admission-information"
                      ? "bg-blue-600 text-white shadow-md"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800"
                  }`}
                >
                  {/* Hospital/Medical Icon */}
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                  </svg>
                  <span>Admission Information</span>
                </button>
              </div>
              {/* Tab Content */}
              <div className="space-y-4">
                {activeTab == "patient-details" && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {" "}
                    {/* Left Column */}
                    <div className="space-y-4">
                      {" "}
                      {/* BASIC INFORMATION */}
                      <div className="bg-gray-50 p-4 rounded-lg">
                        {" "}
                        <h3 className="text-base font-semibold mb-3 flex items-center gap-2 text-gray-">
                          {" "}
                          <FontAwesomeIcon icon={faUser} />
                          Basic Information
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                          {" "}
                          <div>
                            <label className="block text-gray-500 mb-1 font-medium">
                              Member Name
                            </label>
                            <p className="bg-white p-2 rounded border">
                              {memberName || "N/A"}
                            </p>
                          </div>
                          <div>
                            <label className="block text-gray-500 mb-1 font-medium">
                              Member ID
                            </label>
                            <p className="bg-white p-2 rounded border">
                              {member.member_id || "N/A"}
                            </p>
                          </div>
                          <div>
                            <label className="block text-gray-500 mb-1 font-medium">
                              VIP
                            </label>
                            <p className="bg-white p-2 rounded border">
                              {member.vip_tag ? "Yes" : "No"}
                            </p>
                          </div>
                          <div>
                            <label className="block text-gray-500 mb-1 font-medium">
                              Member Type
                            </label>
                            <p className="bg-white p-2 rounded border">
                              {member.member_type || "N/A"}
                            </p>
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-gray-500 mb-1 font-medium">
                              Email Address
                            </label>
                            <p className="bg-white p-2 rounded border">
                              {member.email_add || "N/A"}
                            </p>
                          </div>
                          <div>
                            <label className="block text-gray-500 mb-1 font-medium">
                              Birth Date
                            </label>
                            <p className="bg-white p-2 rounded border">
                              {member.date_of_birth || "N/A"}
                            </p>
                          </div>
                          <div>
                            <label className="block text-gray-500 mb-1 font-medium">
                              Gender
                            </label>
                            <p className="bg-white p-2 rounded border">
                              {member.gender?.toUpperCase() || "N/A"}
                            </p>
                          </div>
                          <div>
                            <label className="block text-gray-500 mb-1 font-medium">
                              Mobile
                            </label>
                            <p className="bg-white p-2 rounded border">
                              {member.mob_tel || "N/A"}
                            </p>
                          </div>
                          <div>
                            <label className="block text-gray-500 mb-1 font-medium">
                              Telephone
                            </label>
                            <p className="bg-white p-2 rounded border">
                              {member.tel_no || "N/A"}
                            </p>
                          </div>
                        </div>
                      </div>
                      {/* CoPay */}
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <h3 className="text-base font-semibold mb-3 flex items-center gap-2 text-blue-800">
                          <FontAwesomeIcon icon={faShieldAlt} />
                          CoPay
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                          <div>
                            <label className="block text-blue-600 mb-0.5 font-medium">
                              Copay c/o Company(%)
                            </label>
                            <p className="bg-white p-1.5 rounded border text-xs">
                              {copayCompany}
                            </p>
                          </div>
                          <div>
                            <label className="block text-blue-600 mb-0.5 font-medium">
                              Copay c/o Member(%)
                            </label>
                            <p className="bg-white p-1.5 rounded border text-xs">
                              {copayMember}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Right Column - similar compact pattern for Plan & Corporate, Room and Board, Member Limit */}
                    {/* ... (abbreviated for brevity; apply p-4, gap-3, text-xs, mb-3, p-1.5 everywhere) */}
                    {/* Right: Plan & Corporate + Room and Board + Member Limit */}
                    <div className="space-y-6">
                      <div className="bg-gray-50 p-4 rounded-xl">
                        <h3 className="text-base font-semibold mb-3 flex items-center gap-2 text-gray-800">
                          <FontAwesomeIcon icon={faStethoscope} />
                          Plan & Corporate Information
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                          <div>
                            <label className="block text-gray-500 mb-1 font-medium">
                              Corporate
                            </label>
                            <p className="bg-white p-2 rounded border">
                              {member.corp_name || "N/A"}
                            </p>
                          </div>
                          <div>
                            <label className="block text-gray-500 mb-1 font-medium">
                              Start Date
                            </label>
                            <p className="bg-white p-2 rounded border">
                              {member.orig_effective_date ||
                                member.effective_date ||
                                "N/A"}
                            </p>
                          </div>
                          <div>
                            <label className="block text-gray-500 mb-1 font-medium">
                              End Date
                            </label>
                            <p className="bg-white p-2 rounded border">
                              {member.expiry_date || "N/A"}
                            </p>
                          </div>
                          <div>
                            <label className="block text-gray-500 mb-1 font-medium">
                              Plan Code
                            </label>
                            <p className="bg-white p-2 rounded border">
                              {member.plan_code || "N/A"}
                            </p>
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-gray-500 mb-1 font-medium">
                              Plan Desc
                            </label>
                            <p className="bg-white p-2 rounded border">
                              {plan.plan_name || "N/A"}
                            </p>
                          </div>
                          <div>
                            <label className="block text-gray-500 mb-1 font-medium">
                              Benefit Type
                            </label>
                            <p className="bg-white p-2 rounded border">
                              Basic {/* From plan_benefits */}
                            </p>
                          </div>
                          <div>
                            <label className="block text-gray-500 mb-1 font-medium">
                              Principal Name
                            </label>
                            <p className="bg-white p-2 rounded border">
                              {member.principal_name || "N/A"}
                            </p>
                          </div>
                          <div>
                            <label className="block text-gray-500 mb-1 font-medium">
                              Principal ID
                            </label>
                            <p className="bg-white p-2 rounded border">
                              {member.principal_id || "N/A"}
                            </p>
                          </div>
                          <div>
                            <label className="block text-gray-500 mb-1 font-medium">
                              PhilHealth #
                            </label>
                            <p className="bg-white p-2 rounded border">
                              {member.philhealth_id || "N/A"}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-green-50 p-4 rounded-xl">
                        <h3 className="font-semibold mb-3 flex items-center gap-2 text-green-800">
                          <FontAwesomeIcon icon={faBed} />
                          Room and Board
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                          <div>
                            <label className="block text-green-600 mb-1 font-medium">
                              Peso Based
                            </label>
                            <div className="grid grid-cols-2 gap-2">
                              <p className="bg-white p-2 rounded border text-center">
                                Basic Limit: {rnb.basic_limit || "N/A"}
                              </p>
                              <p className="bg-white p-2 rounded border text-center">
                                Major Limit: {rnb.major_limit || "N/A"}
                              </p>
                            </div>
                          </div>
                          <div>
                            <label className="block text-green-600 mb-1 font-medium">
                              Category
                            </label>
                            <div className="grid grid-cols-2 gap-2">
                              <p className="bg-white p-2 rounded border text-center">
                                Basic Limit: {rnb.rnb_room_type || "N/A"}
                              </p>
                              <p className="bg-white p-2 rounded border text-center">
                                Major Limit: {rnb.rnb_amt || "N/A"}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Member Limit (from v1 portlet, green border-top) */}
                      <div className="bg-gray-100 p-4 rounded-xl border-t border-green-300">
                        <h3 className="font-semibold mb-3 flex items-center gap-2 text-gray-800">
                          <FontAwesomeIcon icon={faShieldAlt} />
                          Member Limit
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                          <div className="col-md-5">
                            <label className="block text-gray-500 mb-1 font-medium">
                              Remaining Limit
                            </label>
                            <input
                              type="text"
                              value={formData.remainingLimitIcd}
                              disabled
                              className="w-full p-2 bg-white border rounded-md text-right font-mono"
                              placeholder="₱0.00"
                            />
                          </div>
                          <div className="col-md-5 hidden">
                            {" "}
                            {/* Guaranteed Limit hidden in v1 */}
                            <label className="block text-gray-500 mb-1 font-medium">
                              Guaranteed Limit
                            </label>
                            <input
                              type="text"
                              className="w-full p-2 border rounded-md"
                              placeholder="Enter guaranteed limit"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {activeTab == "admission-information" && (
                  <div className="space-y-4">
                    {/* INFORMATION - Completed & Compact */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="text-base font-semibold mb-3 flex items-center gap-2 text-gray-800 text-xs">
                        <FontAwesomeIcon icon={faInfoCircle} />
                        Information
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                        
                        <div>
                          <label className="block text-gray-700 mb-0.5 font-medium required">
                            Amaphil Agent{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={formData.amaphilAgent}
                            onChange={(e) =>
                              handleInputChange("amaphilAgent", e.target.value)
                            }
                            disabled={!editMode}
                            className={`w-full p-1.5 border rounded focus:ring-2 focus:ring-blue-500 text-xs ${
                              editMode ? "" : "bg-gray-100"
                            }`}
                            placeholder="Enter Amaphil Agent"
                          />
                        </div>

                        <div>
                          <label className="block text-gray-700 mb-0.5 font-medium required">
                            Person Spoken (Provider){" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={formData.personSpokenProvider}
                            onChange={(e) =>
                              handleInputChange(
                                "personSpokenProvider",
                                e.target.value
                              )
                            }
                            disabled={!editMode}
                            className={`w-full p-1.5 border rounded focus:ring-2 focus:ring-blue-500 text-xs ${
                              editMode ? "" : "bg-gray-100"
                            }`}
                            placeholder="Enter Person Spoken (Provider)"
                          />
                        </div>

                        <div>
                          <label className="block text-gray-700 mb-0.5 font-medium">
                            Landline
                          </label>
                          <input
                            type="tel"
                            value={formData.landline}
                            onChange={(e) =>
                              handleInputChange("landline", e.target.value)
                            }
                            disabled={!editMode}
                            className={`w-full p-1.5 border rounded focus:ring-2 focus:ring-blue-500 text-xs ${
                              editMode ? "" : "bg-gray-100"
                            }`}
                            placeholder="Enter Landline"
                          />
                        </div>

                        <div>
                          <label className="block text-gray-700 mb-0.5 font-medium required">
                            Person Spoken (Relative){" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={formData.personSpokenRelative}
                            onChange={(e) =>
                              handleInputChange(
                                "personSpokenRelative",
                                e.target.value
                              )
                            }
                            disabled={!editMode}
                            className={`w-full p-1.5 border rounded focus:ring-2 focus:ring-blue-500 text-xs ${
                              editMode ? "" : "bg-gray-100"
                            }`}
                            placeholder="Enter Person Spoken (Relative)"
                          />
                        </div>

                        <div>
                          <label className="block text-gray-700 mb-0.5 font-medium required">
                            Relationship <span className="text-red-500">*</span>
                          </label>
                          <Select
                            value={formData.relationship}
                            onChange={(opt) =>
                              handleSelectChange("relationship", opt)
                            }
                            options={relationshipOptions}
                            placeholder="Select Relationship"
                            disabled={!editMode}
                            styles={customStyles}
                            className="text-xs"
                            isSearchable
                          />
                        </div>

                        <div>
                          <label className="block text-gray-700 mb-0.5 font-medium">
                            PhilHealth Covered?
                          </label>
                          <Select
                            value={formData.philHealthCovered}
                            onChange={(opt) =>
                              handleSelectChange("philHealthCovered", opt)
                            }
                            options={philHealthOptions}
                            placeholder="Select Yes/No"
                            disabled={!editMode}
                            styles={customStyles}
                            className="text-xs"
                          />
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-gray-700 mb-0.5 font-medium required">
                            Department <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={formData.department}
                            onChange={(e) =>
                              handleInputChange("department", e.target.value)
                            }
                            disabled={!editMode}
                            className={`w-full p-1.5 border rounded focus:ring-2 focus:ring-blue-500 text-xs ${
                              editMode ? "" : "bg-gray-100"
                            }`}
                            placeholder="Enter Department"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-gray-700 mb-0.5 font-medium required">
                            Mobile to contact upon discharge{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="tel"
                            value={formData.mobileDischarge}
                            onChange={(e) =>
                              handleInputChange(
                                "mobileDischarge",
                                e.target.value
                              )
                            }
                            disabled={!editMode}
                            className={`w-full p-1.5 border rounded focus:ring-2 focus:ring-blue-500 text-xs ${
                              editMode ? "" : "bg-gray-100"
                            }`}
                            placeholder="Enter Mobile"
                          />
                        </div>
                      </div>
                    </div>
                    {/* ROOM & BOARD BENEFITS - Compact Table */}
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h3 className="text-base font-semibold mb-10 flex items-center gap-10 text-green-800 text-xs">
                        <FontAwesomeIcon icon={faBed} />
                        Room & Board Benefits
                      </h3>
                      {/* <motion.button
                        whileHover={{ scale: 1.05 }}
                        onClick={addRnbRow}
                        disabled={!editMode}
                        className="px-3 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50 flex items-center gap-1 disabled:cursor-not-allowed"
                      >
                        <FontAwesomeIcon icon={faPlus} />
                        Add Row
                      </motion.button> */}
                      <div
                        className={
                          openRnbSelectId ? "overflow-visible" : "overflow-x-auto"
                        }
                      >
                        <table className="w-full bg-white rounded border text-xs">
                          {" "}
                          <thead className="bg-green-100">
                            <tr>
                              <th className="px-3 py-2 text-left font-semibold text-green-800">
                                Room & Board
                              </th>{" "}
                              <th className="px-3 py-2 text-left font-semibold text-green-800">
                                Room No
                              </th>
                              <th className="px-3 py-2 text-left font-semibold text-green-800">
                                Room Rate (₱)
                              </th>
                              <th className="px-3 py-2 text-left font-semibold text-green-800">
                                Days Confined
                              </th>
                              <th className="px-3 py-2 text-left font-semibold text-green-800">
                                Total Fee (₱)
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {formData.rnbRows.map((row) => (
                              <tr key={row.id} className="border-t">
                                <td className="px-3 py-2">
                                  <Select
                                    value={row.roomType}
                                    onChange={(opt) =>
                                      updateRnbRow(row.id, "roomType", opt)
                                    }
                                    options={roomOptions}
                                    placeholder="Select"
                                    isDisabled={!editMode}
                                    menuPortalTarget={
                                      typeof document !== "undefined"
                                        ? document.body
                                        : null
                                    }
                                    menuPosition="fixed"
                                    menuPlacement="auto"
                                    menuShouldScrollIntoView={false}
                                    maxMenuHeight={180}
                                    onMenuOpen={() => setOpenRnbSelectId(row.id)}
                                    onMenuClose={() => setOpenRnbSelectId(null)}
                                    styles={{
                                      ...customStyles,
                                      container: (provided) => ({
                                        ...provided,
                                        minWidth: "160px",
                                      }),
                                      control: (provided) => ({
                                        ...provided,
                                        fontSize: "0.75rem",
                                      }),
                                      menuPortal: (base) => ({
                                        ...base,
                                        zIndex: 2147483647,
                                      }),
                                      menu: (base) => ({
                                        ...base,
                                        zIndex: 2147483647,
                                      }),
                                    }}
                                    className="text-xs"
                                    classNamePrefix="rnb-room-select"
                                  />
                                </td>
                                <td className="px-3 py-2">
                                  <input
                                    type="text"
                                    value={row.roomNo}
                                    onChange={(e) =>
                                      updateRnbRow(
                                        row.id,
                                        "roomNo",
                                        e.target.value
                                      )
                                    }
                                    disabled={!editMode}
                                    className={`w-full p-1 border rounded text-xs ${
                                      editMode ? "" : "bg-gray-100"
                                    }`}
                                    placeholder="Room No"
                                  />
                                </td>
                                <td className="px-3 py-2">
                                  <input
                                    type="number"
                                    value={row.roomRate}
                                    onChange={(e) =>
                                      updateRnbRow(
                                        row.id,
                                        "roomRate",
                                        e.target.value
                                      )
                                    }
                                    disabled={!editMode}
                                    className={`w-full p-1 border rounded text-xs ${
                                      editMode ? "" : "bg-gray-100"
                                    }`}
                                    placeholder="0.00"
                                    step="0.01"
                                  />
                                </td>
                                <td className="px-3 py-2">
                                  <input
                                    type="number"
                                    value={row.daysConfined}
                                    onChange={(e) =>
                                      updateRnbRow(
                                        row.id,
                                        "daysConfined",
                                        e.target.value
                                      )
                                    }
                                    disabled={!editMode}
                                    className={`w-full p-1 border rounded text-xs ${
                                      editMode ? "" : "bg-gray-100"
                                    }`}
                                    placeholder="1"
                                    min="1"
                                  />
                                </td>
                                <td className="px-3 py-2 font-medium text-green-700 text-right text-xs">
                                  {row.totalFee.toFixed(2)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    {/* PHYSICIAN DETAILS - Compact */}
                    <div className="bg-indigo-50 p-4 rounded-lg">
                      <h3 className="text-base font-semibold mb-3 flex items-center gap-2 text-indigo-800 text-xs">
                        <FontAwesomeIcon icon={faUserMd} />
                        Physician Details
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                        <div>
                          <label className="block text-indigo-700 mb-0.5 font-medium required">
                            Chief Complaint{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <textarea
                            value={formData.chiefComplaint}
                            onChange={handleChiefComplaintChange}
                            rows={3}
                            disabled={!editMode}
                            className={`w-full p-1.5 border rounded focus:ring-2 focus:ring-indigo-500 resize-none text-xs ${
                              editMode ? "" : "bg-gray-100"
                            } ${chiefComplaintError ? "border-red-500" : ""}`}
                            placeholder="Enter chief complaint (min 2, max 1000 chars)"
                            maxLength={1000}
                          />
                          {chiefComplaintError && (
                            <p className="text-red-500 text-xs mt-0.5">
                              {chiefComplaintError}
                            </p>
                          )}
                          <div
                            className={`text-right text-xs mt-0.5 ${
                              formData.chiefComplaint.length > 900
                                ? "text-yellow-600"
                                : formData.chiefComplaint.length > 950
                                ? "text-red-600"
                                : "text-gray-500"
                            }`}
                          >
                            {formData.chiefComplaint.length}/1000
                          </div>
                        </div>
                        <div>
                          <label className="block text-indigo-700 mb-0.5 font-medium required">
                            Attending Physician{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <AsyncSelect
                            isMulti
                            cacheOptions
                            defaultOptions
                            loadOptions={loadPhysicianOptions}
                            value={formData.attendingPhysician}
                            onChange={(opts) =>
                              handleSelectChange("attendingPhysician", opts)
                            }
                            placeholder="Search & Select Physician"
                            isDisabled={!editMode}
                            styles={customStyles}
                            className="text-xs"
                            debounceTimeout={300}
                          />
                        </div>
                        {/* <div className="md:col-span-2">
                          <label className="block text-indigo-700 mb-0.5 font-medium required">
                            Initial Diagnosis{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <AsyncSelect
                            cacheOptions
                            defaultOptions
                            loadOptions={loadIcdOptions}
                            value={formData.initialDiagnosis}
                            onChange={(opt) =>
                              handleSelectChange("initialDiagnosis", opt)
                            }
                            placeholder="Search ICD (min 2 chars)"
                            disabled={!editMode}
                            styles={customStyles}
                            className="text-xs"
                            debounceTimeout={300}
                          />
                        </div> */}
                        {/* Diagnosis ICD */}
                        <div className="space-y-1.5">
                          <label className="block text-indigo-700 mb-0.5 font-medium required">
                            (ICD Codes) Initial Diagnosis{" "}
                            <span className="text-red-500">*</span>
                            <FontAwesomeIcon
                              icon={faFilter}
                              className="text-xs text-gray-400"
                            />
                          </label>
                          <AsyncSelect
                            cacheOptions
                            defaultOptions // Load initial on focus
                            loadOptions={loadIcdOptions}
                            value={formData.initialDiagnosis}
                            onChange={(opt) =>
                              handleSelectChange("initialDiagnosis", opt)
                            }
                            placeholder="Type to search ICD (min 2 chars)..."
                            loadingMessage={() => "Searching ICDs..."}
                            noOptionsMessage={() => "Type to search ICDs"}
                            styles={customStyles}
                            className="text-xs"
                            debounceTimeout={300}
                            disabled={!editMode}
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-indigo-700 mb-0.5 font-medium">
                            Remaining Limit for Selected ICD
                          </label>
                          <input
                            type="text"
                            value={formData.remainingLimitIcd}
                            disabled
                            className="w-full p-1.5 bg-gray-100 border rounded-md text-xs text-right"
                          />
                        </div>
                      </div>
                    </div>
                    {/* Remarks & Reason */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-gray-700 mb-0.5 font-medium">
                          Remarks
                        </label>
                        <textarea
                          value={formData.remark}
                          onChange={(e) =>
                            handleInputChange("remark", e.target.value)
                          }
                          rows={2}
                          disabled={!editMode}
                          className={`w-full p-1.5 border rounded focus:ring-2 focus:ring-blue-500 resize-none text-xs ${
                            editMode ? "" : "bg-gray-100"
                          }`}
                          placeholder="Enter remarks"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-700 mb-0.5 font-medium">
                          Reason
                        </label>
                        <textarea
                          value={formData.reason}
                          onChange={(e) =>
                            handleInputChange("reason", e.target.value)
                          }
                          rows={2}
                          disabled={!editMode}
                          className={`w-full p-1.5 border rounded focus:ring-2 focus:ring-blue-500 resize-none text-xs ${
                            editMode ? "" : "bg-gray-100"
                          }`}
                          placeholder="Enter reason"
                        />
                      </div>
                    </div>

                    {/* Cover Radio */}
                    {/* <div className="bg-yellow-50 p-3 rounded border">
                      {" "}
                      <label className="block text-yellow-800 mb-1.5 font-medium flex items-center gap-1 text-xs">
                        <FontAwesomeIcon icon={faQuestionCircle} />
                        Cover the admission?
                      </label>
                      <div className="flex gap-3">
                        <label className="flex items-center text-xs">
                          <input
                            type="radio"
                            name="coverable_tag"
                            value="1"
                            checked={formData.coverableTag == "1"}
                            onChange={() => handleRadioChange("1")}
                            disabled={!editMode}
                            className="mr-1"
                          />
                          Yes
                        </label>
                        <label className="flex items-center text-xs">
                          <input
                            type="radio"
                            name="coverable_tag"
                            value="0"
                            checked={formData.coverableTag == "0"}
                            onChange={() => handleRadioChange("0")}
                            disabled={!editMode}
                            className="mr-1"
                          />
                          No
                        </label>
                      </div>
                    </div> */}

                  </div>
                )}
                {/* Footer */}
                <div className="sticky bottom-0 z-30 -mx-4 mt-6 flex justify-end gap-2 border-t border-gray-200 bg-white/95 px-4 py-3 shadow-[0_-8px_18px_rgba(15,23,42,0.08)] backdrop-blur">
                  <Type3ContinueModalActionButton
                    formData={formData}
                    editMode={editMode}
                    onClose={onClose}
                    toggleEdit={toggleEdit}
                    handleSave={handleSave}
                    handleVerify={handleVerify}
                  />
                </div>
              </div>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
export default Type3ContinueModal;
